import { getHazardName } from "@/features/branding";
import { HazardPlusTotal, Year } from "@/features/pe_aldd";
import { PERCENT_METRICS, type MetricKey } from "@/features/pe_aldd/format";
import { AnaHazardId } from "@/store/services/facades/types";
import { PeaSummaryResponse } from "@/store/services/facades/types/pea";
import { Rcp } from "@/types/rcp";
import { displayEpsilon, getAbsoluteValue } from "@/utils/number";

export * from "@/features/pe_aldd/format";

// ---- Shared types ----
export type SelectedYearRow = { hazard: HazardPlusTotal; label: string; value: number };

export const CURRENCY_METRICS: MetricKey[] = [
  "ttip",
  "cav",
  "total_market_value",
  "average_market_value",
  "total_replacement_cost",
  "average_replacement_cost",
];

export type FixedMetric = "count" | "currency" | "percent";

export type Metric = MetricKey | FixedMetric;

export const multiplierForMetric = (metric: Metric): number => {
  if (metric === "percent" || PERCENT_METRICS.some((m) => m === metric)) {
    return 100;
  }

  return 1;
};

export const unitForMetric = (metric: Metric): "%" | "$" => {
  if (metric === "currency" || CURRENCY_METRICS.some((m) => m === metric)) {
    return "$";
  }

  return "%";
};

const METRIC_SHORT_LABELS: Partial<Record<MetricKey, string>> = {
  avg_damage_mtd: "MVAR",
  productivity_loss: "PL",
  avg_failure_mtd: "FP",
  ttip: "TTIP",
};

export const getMetricShortLabel = (metric: MetricKey, isPointLevel = false): string =>
  metric === "ttip" && isPointLevel ? "TIP" : (METRIC_SHORT_LABELS[metric] ?? "");

// ---- Shared helpers ----
export const getHazardsByScenario = (data: PeaSummaryResponse, rcp?: Rcp | null) =>
  data.filter((r) => (!rcp || r.climate_scenario === rcp) && (r.hazard as HazardPlusTotal) !== "total");

export const getHazardsByScenarioIncludeTotal = (data: PeaSummaryResponse, rcp?: Rcp | null) =>
  data.filter((r) => !rcp || r.climate_scenario === rcp);

export const filterResultsByMetric = ({
  data,
  rcp,
  year,
  metric,
  includeTotal = false,
}: {
  data: PeaSummaryResponse;
  rcp: Rcp;
  year: Year;
  metric: MetricKey;
  includeTotal?: boolean;
}): SelectedYearRow[] => {
  const hazards = includeTotal ? getHazardsByScenarioIncludeTotal(data, rcp) : getHazardsByScenario(data, rcp);
  const multiplier = multiplierForMetric(metric);
  return hazards
    .map((r) => {
      const value = (Number((r as any)[metric]?.[year] ?? 0) * multiplier) as number;
      const label = getHazardName(r.hazard as AnaHazardId) ?? String(r.hazard);
      return { hazard: r.hazard, label, value } as const;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export type MetricMaxima = {
  avgDamageMtd: number;
  avgFailureMtd: number;
  productivityLoss: number;
  ttip: number;
};

export const computeMetricMaximaAcrossAllScenarios = (data: PeaSummaryResponse): MetricMaxima => {
  let avgDamageMax = 0;
  let avgFailureMax = 0;
  let productivityLossMax = 0;
  let ttipMax = 0;

  // Assume RCP8.5 has the highest values for all metrics for efficiency
  const rows = getHazardsByScenario(data, "rcp85");

  rows.forEach((r) => {
    Object.values(r.avg_damage_mtd).forEach((v) => (avgDamageMax = Math.max(avgDamageMax, (v ?? 0) * 100)));
    Object.values(r.avg_failure_mtd).forEach((v) => (avgFailureMax = Math.max(avgFailureMax, (v ?? 0) * 100)));
    Object.values(r.productivity_loss).forEach(
      (v) => (productivityLossMax = Math.max(productivityLossMax, (v ?? 0) * 100))
    );
    // TTIP is nullable and uses multiplier 1 bc $
    if (r.ttip) {
      Object.values(r.ttip).forEach((v) => (ttipMax = Math.max(ttipMax, v ?? 0)));
    }
  });

  return {
    avgDamageMtd: avgDamageMax || 0,
    avgFailureMtd: avgFailureMax || 0,
    productivityLoss: productivityLossMax || 0,
    ttip: ttipMax || 0,
  };
};

export const computeAbsoluteChange = (
  baseline: number | null,
  adapted: number | null,
  absolute = false
): number | null => {
  if (baseline === null || adapted === null) return null;
  const delta = adapted - baseline;
  return absolute ? getAbsoluteValue(delta) : delta;
};

/** Absorbs float noise from the ×100 percent conversions. */
const EPS_REL = 1e-9;

/**
 * A change smaller than one displayed digit is not a change. Tested on the delta, so the arrow can
 * never claim movement the number prints as zero. Callers pass display units.
 */
export const isEffectivelyUnchanged = (baseline: number, adapted: number, unit: "%" | "$" = "%"): boolean => {
  const delta = Math.abs(adapted - baseline);
  if (delta < displayEpsilon(unit)) return true;
  return delta <= EPS_REL * Math.max(Math.abs(baseline), Math.abs(adapted));
};

/**
 * Relative reduction as a percentage: positive means the metric fell. Callers pass display units.
 */
export const computeRelativeChange = (
  baseline: number | null,
  adapted: number | null,
  unit: "%" | "$" = "%"
): number | null => {
  if (baseline === null || adapted === null) return null;
  if (!Number.isFinite(baseline) || !Number.isFinite(adapted)) return null;

  // keep before the denominator guard, so 0 to 0 change is 0% instead of null.
  if (isEffectivelyUnchanged(baseline, adapted, unit)) return 0;

  if (Math.abs(baseline) < displayEpsilon(unit)) return null;
  return ((baseline - adapted) / baseline) * 100;
};

/**
 * Keys of the items in the top `percentile` of absolute change.
 *
 * - change must exceed zero, so a hazard/year where there is no absolute change is not selected as 'top'
 */
export const selectTopPercentileKeys = (
  items: { key: string; absChange: number | null }[],
  percentile = 0.1
): Set<string> => {
  const rankable = items.filter((i) => i.absChange != null && Number.isFinite(i.absChange));
  if (rankable.length === 0) return new Set();

  const K = Math.ceil(percentile * rankable.length);

  // Float64Array#sort is numeric-ascending, so the Kth largest is indexed from the end.
  const sorted = Float64Array.from(rankable, (i) => Math.abs(i.absChange as number)).sort();
  const cutoff = sorted[sorted.length - K];

  const keys = new Set<string>();
  for (const i of rankable) {
    // absolute change is negative for adapted
    const change = Math.abs(i.absChange as number);
    if (change >= cutoff && change > 0) keys.add(i.key);
  }

  return keys;
};

/** MVAR is not calculated for heat, and TIP and ABC ratings derive from it. */
export const isMvarDerivedDataAvailable = (hazard: HazardPlusTotal) => hazard !== "heat";

const MVAR_DERIVED_METRICS: MetricKey[] = ["avg_damage_mtd", "ttip"];

export const isHazardMetricDataAvailable = (hazard: HazardPlusTotal, metric: MetricKey) =>
  !MVAR_DERIVED_METRICS.includes(metric) || isMvarDerivedDataAvailable(hazard);
